import { User } from 'fitify-types/src/types/user'
import { isDefined } from 'fitify-utils/src/properties'

import { DEFAULT_PROMO_CODE } from 'data/promo'
import type { FunnelRoutesDefinition } from 'types/routes'
import { type DataPathState, Funnel, Question } from 'types/state'

import { getSubscriptionFlowPages } from '../../utils/funnel-pages'

type TCHM01DataState = DataPathState<Funnel.TCHM01>

const pages = {
  welcomeAgeBuckets: {
    path: '/tchm01/welcome-age-buckets',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    isExcludedFromProgressBarOrStepper: true,
    stateKey: 'age_bucket',
    id: 'welcome_age_buckets',
  },
  socialProofMillions: {
    path: '/tchm01/social-proof-millions',
    isValidatedStep: true,
    id: 'social_proof_millions',
  },
  experience: {
    path: '/tchm01/experience',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'experience',
    id: 'experience',
  },
  experiencePitch: {
    path: '/tchm01/experience-pitch',
    isValidatedStep: true,
    id: 'experience_pitch',
  },
  mainGoal: {
    path: '/tchm01/main-goal',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'goal',
    id: 'main_goal',
    required: true,
  },
  goalPitch: {
    path: '/tchm01/goal-pitch',
    isValidatedStep: true,
    id: 'goal_pitch',
    condition: (data: TCHM01DataState) => {
      return isDefined(data.goal)
    },
  },
  motivationEarly: {
    path: '/tchm01/motivation-early',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'motivation_early',
    id: 'motivation_early',
  },
  didYouKnowHormones: {
    path: '/tchm01/did-you-know-hormones',
    isValidatedStep: true,
    id: 'did_you_know_hormones',
  },
  didYouKnowDepression: {
    path: '/tchm01/did-you-know-depression',
    isValidatedStep: true,
    id: 'did_you_know_depression',
  },
  didYouKnowLongevity: {
    path: '/tchm01/did-you-know-longevity',
    isValidatedStep: true,
    id: 'did_you_know_longevity',
  },
  physicalBuild: {
    path: '/tchm01/physical-build',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'current_shape',
    id: 'physical_build',
  },
  bodyGoal: {
    path: '/tchm01/body-goal',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'body_goal',
    id: 'body_goal',
  },
  weightChangeExp: {
    path: '/tchm01/weight-change-exp',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'weight_change_exp',
    id: 'weight_change_exp',
  },
  bodyTypePitch: {
    path: '/tchm01/body-type-pitch',
    isValidatedStep: true,
    id: 'body_type_pitch',
    condition: (data: TCHM01DataState) => {
      return isDefined(data.weight_change_exp)
    },
  },
  bestShape: {
    path: '/tchm01/best-shape',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'latest_ideal_weight',
    id: 'best_shape',
  },
  consistencyPitch: {
    path: '/tchm01/consistency-pitch',
    isValidatedStep: true,
    id: 'consistency_pitch',
  },
  flexibility: {
    path: '/tchm01/flexibility',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'flexibility',
    id: 'flexibility',
  },
  posturePitch: {
    path: '/tchm01/posture-pitch',
    isValidatedStep: true,
    id: 'posture_pitch',
  },
  problemAreas: {
    path: '/tchm01/problem-areas',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'problem_areas',
    id: 'problem_areas',
    required: true,
  },
  problemAreasPitch: {
    path: '/tchm01/problem-areas-pitch',
    isValidatedStep: true,
    id: 'problem_areas_pitch',
  },
  struggle: {
    path: '/tchm01/struggle',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'struggle',
    id: 'struggle',
  },
  strugglePitch: {
    path: '/tchm01/struggle-pitch',
    isValidatedStep: true,
    id: 'struggle_pitch',
    condition: (data: TCHM01DataState) => {
      return Boolean(
        data.struggle?.length &&
        data.struggle.some((item) => item !== Question.Struggle.None)
      )
    },
  },
  stairsCardio: {
    path: '/tchm01/stairs-cardio',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'stairs_cardio',
    id: 'stairs_cardio',
  },
  howOftenExercise: {
    path: '/tchm01/how-often-exercise',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'how_often_exercise',
    id: 'how_often_exercise',
  },
  exerciseBarrier: {
    path: '/tchm01/exercise-barrier',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'exercise_barrier',
    id: 'exercise_barrier',
    condition: (data: TCHM01DataState) => {
      const howOften = data.how_often_exercise
      return (
        howOften === Question.HowOftenExercise.Never ||
        howOften === Question.HowOftenExercise.Monthly
      )
    },
  },
  workoutFrequency: {
    path: '/tchm01/workout-frequency',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'workout_frequency',
    id: 'workout_frequency',
  },
  workoutLength: {
    path: '/tchm01/workout-length',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'workout_length',
    id: 'workout_length',
  },
  bigResultsPitch: {
    path: '/tchm01/big-results-pitch',
    isValidatedStep: true,
    id: 'big_results_pitch',
  },
  howOftenWalks: {
    path: '/tchm01/how-often-walks',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'how_often_walks',
    id: 'how_often_walks',
  },
  workSchedule: {
    path: '/tchm01/work-schedule',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'work_schedule',
    id: 'work_schedule',
  },
  stress: {
    path: '/tchm01/stress',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'stress',
    id: 'stress',
  },
  cortisolPitch: {
    path: '/tchm01/cortisol-pitch',
    isValidatedStep: true,
    id: 'cortisol_pitch',
  },
  typicalDayActivity: {
    path: '/tchm01/typical-day-activity',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'typical_day_activity',
    id: 'typical_day_activity',
  },
  energyLevels: {
    path: '/tchm01/energy-levels',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'energy_levels',
    id: 'energy_levels',
  },
  energyPitch: {
    path: '/tchm01/energy-pitch',
    isValidatedStep: true,
    id: 'energy_pitch',
  },
  waterIntake: {
    path: '/tchm01/water-intake',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'water_intake',
    id: 'water_intake',
  },
  sleepDuration: {
    path: '/tchm01/sleep-duration',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'sleep_duration',
    id: 'sleep_duration',
  },
  diet: {
    path: '/tchm01/diet',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'diet',
    id: 'diet',
  },
  habits: {
    path: '/tchm01/habits',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'habits',
    id: 'habits',
  },
  cravings: {
    path: '/tchm01/cravings',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'cravings',
    id: 'cravings',
  },
  smoking: {
    path: '/tchm01/smoking',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'smoking',
    id: 'smoking',
  },
  alcohol: {
    path: '/tchm01/alcohol',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'alcohol',
    id: 'alcohol',
  },
  lifeEventsStopped: {
    path: '/tchm01/life-events-stopped',
    cluster: 'cluster_almost_there',
    isValidatedStep: true,
    stateKey: 'life_events_stopped',
    id: 'life_events_stopped',
  },
  eligibilityPitch: {
    path: '/tchm01/eligibility-pitch',
    isValidatedStep: true,
    id: 'eligibility_pitch',
  },
  height: {
    path: '/tchm01/height',
    cluster: 'cluster_almost_there',
    isValidatedStep: true,
    stateKey: 'height',
    id: 'height',
    required: true,
  },
  weight: {
    path: '/tchm01/weight',
    cluster: 'cluster_almost_there',
    isValidatedStep: true,
    stateKey: 'weight',
    id: 'weight',
    required: true,
  },
  goalWeight: {
    path: '/tchm01/goal-weight',
    cluster: 'cluster_almost_there',
    isValidatedStep: true,
    stateKey: 'goal_weight',
    id: 'goal_weight',
    required: true,
  },
  age: {
    path: '/tchm01/age',
    cluster: 'cluster_almost_there',
    isValidatedStep: true,
    stateKey: 'age',
    id: 'age',
    required: true,
  },
  wellnessProfile: {
    path: '/tchm01/wellness-profile',
    isValidatedStep: true,
    id: 'wellness_profile',
    condition: (data: TCHM01DataState) => {
      return (
        isDefined(data.typical_day_activity) &&
        isDefined(data.current_shape) &&
        isDefined(data.how_often_exercise) &&
        isDefined(data.weight_change_exp)
      )
    },
  },
  lifeEvent: {
    path: '/tchm01/life-event',
    cluster: 'cluster_almost_there',
    isValidatedStep: true,
    stateKey: 'life_event',
    id: 'life_event',
  },
  lifeEventDate: {
    path: '/tchm01/life-event-date',
    isValidatedStep: true,
    stateKey: 'life_event_date',
    id: 'life_event_date',
    condition: (data: TCHM01DataState) => {
      return (
        isDefined(data.life_event) &&
        data.life_event !== User.Profile.LifeEvent.NoEvent
      )
    },
  },
  lifeEventGraph: {
    path: '/tchm01/life-event-graph',
    isValidatedStep: true,
    id: 'life_event_graph',
    condition: (data: TCHM01DataState) => {
      return isDefined(data.goal_weight) && isDefined(data.weight)
    },
  },
  credibilityPitch: {
    path: '/tchm01/credibility-pitch',
    isValidatedStep: true,
    id: 'credibility_pitch',
  },
  planGenerator: {
    path: '/tchm01/plan-generator',
    id: 'plan_generator',
  },
  email: {
    path: '/tchm01/email',
    id: 'email',
    stateKey: 'email',
  },
  name: {
    path: '/tchm01/name',
    isValidatedStep: true,
    stateKey: 'displayName',
    id: 'name',
  },
  namePitch: {
    path: '/tchm01/name-pitch',
    isValidatedStep: true,
    id: 'name_pitch',
  },
  discountReveal: {
    path: '/tchm01/discount-reveal',
    isValidatedStep: true,
    id: 'discount_reveal',
    condition: (_, { promoCode }) => {
      return Boolean(!promoCode || promoCode === DEFAULT_PROMO_CODE)
    },
  },
  ...getSubscriptionFlowPages(Funnel.TCHM01),
} as const satisfies FunnelRoutesDefinition<TCHM01DataState>['pages']

const pagesMap = Object.values(pages)

const detectLocationPagesMap = [
  pages.planGenerator,
  pages.subscription,
  pages.exclusiveOffer,
] as const satisfies FunnelRoutesDefinition<TCHM01DataState>['detectLocationPagesMap']

const noLeaveDialogPagesMap = [
  pages.subscription,
  pages.exclusiveOffer,
  pages.success,
] as const satisfies FunnelRoutesDefinition<TCHM01DataState>['noLeaveDialogPagesMap']

export const TCHM01_ROUTES = {
  detectLocationPagesMap,
  noLeaveDialogPagesMap,
  pages,
  pagesMap,
} as const satisfies FunnelRoutesDefinition<TCHM01DataState>
